import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Clock, FileText, File, CheckCircle, ExternalLink, Sparkles, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { DiagnosisCodeSelector } from '@/components/session/DiagnosisCodeSelector';
import { SuperbillGenerator } from '@/components/session/SuperbillGenerator';
import { toast } from 'sonner';

interface NotesTabProps {
  booking: {
    id: string;
    client_id: string;
    practitioner_id: string;
    start_time: string;
    end_time: string;
    status: string;
    format: string;
    clients?: {
      first_name: string;
      last_name: string;
    };
    practitioners?: {
      first_name: string;
      last_name: string;
    };
  };
}

export function NotesTab({ booking }: NotesTabProps) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState<string[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [isAiPrefilling, setIsAiPrefilling] = useState(false);

  // Auto-save to localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`session-notes-${booking.id}`);
    if (saved) {
      setNotes(saved);
    }
    // Load saved codes
    const savedIcd = localStorage.getItem(`session-icd-${booking.id}`);
    const savedCpt = localStorage.getItem(`session-cpt-${booking.id}`);
    if (savedIcd) setSelectedIcdCodes(JSON.parse(savedIcd));
    if (savedCpt) setSelectedCptCodes(JSON.parse(savedCpt));
  }, [booking.id]);

  useEffect(() => {
    if (notes) {
      localStorage.setItem(`session-notes-${booking.id}`, notes);
    }
  }, [notes, booking.id]);

  // Save codes to localStorage
  useEffect(() => {
    localStorage.setItem(`session-icd-${booking.id}`, JSON.stringify(selectedIcdCodes));
  }, [selectedIcdCodes, booking.id]);

  useEffect(() => {
    localStorage.setItem(`session-cpt-${booking.id}`, JSON.stringify(selectedCptCodes));
  }, [selectedCptCodes, booking.id]);

  // Warn before page unload if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (notes && !lastSaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [notes, lastSaved]);

  const sessionDate = new Date(booking.start_time).toLocaleDateString();
  const clientName = `${booking.clients?.first_name || ''} ${booking.clients?.last_name || ''}`.trim();

  // --- AI Pre-fill ---
  const handleAiPrefill = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a note template first');
      return;
    }

    setIsAiPrefilling(true);
    try {
      // Find transcript for this booking
      const { data: transcripts } = await supabase
        .from('session_transcripts')
        .select('id, transcript_text')
        .eq('booking_id', booking.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (!transcripts || transcripts.length === 0) {
        toast.error('No transcript found for this session. Record a session first.');
        return;
      }

      const transcript = transcripts[0];
      const { data, error } = await supabase.functions.invoke('process-session-transcript', {
        body: {
          transcriptId: transcript.id,
          transcriptText: transcript.transcript_text,
          reportType: selectedTemplate,
          clientName,
          serviceName: booking.format,
          sessionDate: booking.start_time,
        },
      });

      if (error) throw error;

      if (data?.analysis) {
        // Convert structured AI output to template text
        let prefilled = '';
        if (typeof data.analysis === 'string') {
          prefilled = data.analysis;
        } else {
          prefilled = Object.entries(data.analysis)
            .map(([key, value]) => {
              const heading = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const body = typeof value === 'string' ? value 
                : Array.isArray(value) ? (value as any[]).map((v: any) => typeof v === 'string' ? `- ${v}` : `- ${JSON.stringify(v)}`).join('\n')
                : JSON.stringify(value, null, 2);
              return `${heading}:\n${body}`;
            })
            .join('\n\n');
        }
        setNotes(prefilled);
        toast.success('AI pre-fill complete! Review and edit as needed.');
      }
    } catch (err: any) {
      console.error('AI pre-fill error:', err);
      toast.error('AI pre-fill failed. Please try again.');
    } finally {
      setIsAiPrefilling(false);
    }
  };

  const templates = [
    {
      id: 'soap',
      name: 'SOAP Note (Clinical/Insurance)',
      content: `SOAP Note — ${sessionDate}
Client: ${clientName}
Session Format: ${booking.format}

═══════════════════════════════════════
S — SUBJECTIVE
(Client's self-reported complaints, feelings, and concerns in their own words)

Chief Complaint:
- 

History of Present Issue:
- 

Client's Reported Mood/State:
- 

═══════════════════════════════════════
O — OBJECTIVE
(Observable, measurable findings during the session)

Appearance & Behavior:
- Affect: 
- Eye Contact: 
- Speech: 
- Psychomotor Activity: 

Mental Status Observations:
- Orientation: 
- Thought Process: 
- Insight/Judgment: 

Relevant Assessment Scores (if applicable):
- 

═══════════════════════════════════════
A — ASSESSMENT
(Clinical impression and analysis based on S and O)

Formulation:
- 

Functional Impairment Level: (Mild / Moderate / Severe)

Risk Assessment:
- Suicidal Ideation: Yes / No
- Self-Harm: Yes / No
- Harm to Others: Yes / No

═══════════════════════════════════════
P — PLAN
(Next steps, interventions, and follow-up)

Interventions Used This Session:
- 

Homework/Assignments:
- 

Referrals (if any):
- 

Next Session: 
Frequency: `
    },
    {
      id: 'dap',
      name: 'DAP Note (Counseling)',
      content: `DAP Note — ${sessionDate}
Client: ${clientName}

═══════════════════════════════════════
D — DATA
(What happened in the session — both subjective and objective)

Client Presentation:
- 

Topics Discussed:
- 

Client Statements (key quotes):
- 

Observable Behavior:
- 

═══════════════════════════════════════
A — ASSESSMENT
(Therapist's clinical interpretation)

Progress Toward Goals:
- Goal 1: (Progressing / Stable / Regressing)
- Goal 2: (Progressing / Stable / Regressing)

Clinical Impressions:
- 

Themes & Patterns Identified:
- 

Risk Level: (Low / Moderate / High)

═══════════════════════════════════════
P — PLAN
(Action items and next steps)

Interventions for Next Session:
- 

Client Homework:
- 

Coordination/Referrals:
- 

Next Appointment: `
    },
    {
      id: 'birp',
      name: 'BIRP Note (Behavioral Health)',
      content: `BIRP Note — ${sessionDate}
Client: ${clientName}

═══════════════════════════════════════
B — BEHAVIOR
(Observable behavior and client presentation)

Presenting Behavior:
- 

Mood/Affect:
- 

Engagement Level: (High / Moderate / Low / Resistant)

Significant Statements:
- 

═══════════════════════════════════════
I — INTERVENTION
(What the practitioner did during the session)

Therapeutic Techniques Used:
- 

Psychoeducation Provided:
- 

Skills Taught/Practiced:
- 

═══════════════════════════════════════
R — RESPONSE
(How the client responded to interventions)

Client Response to Interventions:
- 

Changes Observed During Session:
- 

Homework Compliance (from previous session):
- (Completed / Partial / Not Completed)

═══════════════════════════════════════
P — PLAN
(What happens next)

Continued Treatment Focus:
- 

New Goals/Modifications:
- 

Next Session: `
    },
    {
      id: 'session-summary',
      name: 'Session Summary (Wellness/Client-Facing)',
      content: `Session Summary — ${sessionDate}
Client: ${clientName}
Session Type: ${booking.format}

═══════════════════════════════════════
SESSION OVERVIEW

What We Explored Today:
- 

Key Themes:
- 

═══════════════════════════════════════
KEY FINDINGS & INSIGHTS

What Came Up:
- 

Breakthroughs/Shifts:
- 

Areas for Further Exploration:
- 

═══════════════════════════════════════
RECOMMENDATIONS

Practices to Try Before Next Session:
- 

Self-Care Suggestions:
- 

Resources/Reading:
- 

═══════════════════════════════════════
NEXT SESSION

Planned Focus:
- 

Suggested Timeframe: `
    },
    {
      id: 'progress',
      name: 'Progress Note (Wellness)',
      content: `Progress Note — ${sessionDate}
Client: ${clientName}
Session #: 

═══════════════════════════════════════
PROGRESS OVERVIEW

Current State:
- 

Changes Since Last Session:
- (Improved / Same / Declined)

Specific Improvements:
- 

═══════════════════════════════════════
SESSION WORK

Modalities/Techniques Used:
- 

Client Engagement & Receptivity:
- 

Client's Self-Reported Experience:
- 

═══════════════════════════════════════
OBSERVATIONS

Practitioner Observations:
- 

Patterns/Trends Across Sessions:
- 

═══════════════════════════════════════
NEXT STEPS

Priority Focus Areas:
- 

Recommended Practices:
- 

Next Session: `
    }
  ];

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setNotes(template.content);
      setSelectedTemplate(templateId);
    }
  };

  const handleSave = async () => {
    if (!notes.trim()) {
      return;
    }

    setIsSaving(true);

    try {
      const { data, error } = await supabase
        .from('medical_notes')
        .insert({
          title: `Session Notes - ${booking.clients?.first_name} ${booking.clients?.last_name} - ${new Date(booking.start_time).toLocaleDateString()}`,
          content: notes,
          note_type: selectedTemplate || 'session',
          practitioner_id: booking.practitioner_id,
          client_id: booking.client_id,
          booking_id: booking.id,
          is_private: false,
          tags: [],
          icd_codes: selectedIcdCodes,
          cpt_codes: selectedCptCodes,
        } as any)
        .select()
        .single();

      if (error) throw error;

      setLastSaved(new Date());
      setShowSavedMessage(true);

      // Clear localStorage after successful save
      localStorage.removeItem(`session-notes-${booking.id}`);
      localStorage.removeItem(`session-icd-${booking.id}`);
      localStorage.removeItem(`session-cpt-${booking.id}`);

      setTimeout(() => {
        setShowSavedMessage(false);
      }, 10000);
    } catch (error) {
      console.error('Error saving session notes:', error);
      alert('Failed to save notes to database. Notes are saved locally.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatLastSaved = () => {
    if (!lastSaved) return 'Not saved';
    return `Saved at ${lastSaved.toLocaleTimeString()}`;
  };

  return (
    <div className="space-y-6 pb-4">
      {/* Success Message */}
      {showSavedMessage && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-green-900">Session notes saved successfully!</p>
                <p className="text-sm text-green-700 mt-1">
                  Your notes have been saved and can be viewed in the{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-green-700 underline font-medium"
                    onClick={() => navigate('/practitioner-dashboard?tab=reports')}
                  >
                    Reports section
                    <ExternalLink className="h-3 w-3 ml-1 inline" />
                  </Button>
                  {' '}of your dashboard.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <File className="h-5 w-5" />
            Note Templates
          </CardTitle>
          <CardDescription>
            Choose a template to get started with structured note-taking
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Select a note template..." />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* AI Pre-fill Button */}
          {selectedTemplate && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAiPrefill}
              disabled={isAiPrefilling}
              className="w-full"
            >
              {isAiPrefilling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  AI analyzing transcript...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Pre-fill from Transcript
                </>
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Notes Editor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Session Notes
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {formatLastSaved()}
              </Badge>
              <Button 
                onClick={handleSave}
                disabled={isSaving || !notes.trim()}
                size="sm"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Document session details, observations, and session plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Start typing your session notes here..."
            className="min-h-[40vh] sm:min-h-[400px] font-mono text-sm"
          />
        </CardContent>
      </Card>

      {/* ICD-10 / CPT Code Selector */}
      <DiagnosisCodeSelector
        selectedIcdCodes={selectedIcdCodes}
        selectedCptCodes={selectedCptCodes}
        onIcdCodesChange={setSelectedIcdCodes}
        onCptCodesChange={setSelectedCptCodes}
      />

      {/* Superbill Generator */}
      <SuperbillGenerator
        bookingId={booking.id}
        practitionerId={booking.practitioner_id}
        clientId={booking.client_id}
        icdCodes={selectedIcdCodes}
        cptCodes={selectedCptCodes}
      />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Common note sections and shortcuts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[
              'Assessment',
              'Session Plan',
              'Homework',
              'Next Session',
              'Risk Factors',
              'Progress',
              'Supplements',
              'Referrals'
            ].map((section) => (
              <Button
                key={section}
                variant="outline"
                size="sm"
                onClick={() => setNotes(notes + `\n\n${section}:\n- `)}
                className="justify-start text-xs sm:text-sm"
              >
                {section}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
